import React, { useEffect, useRef, useState } from "react";
import { Grid } from "@mui/material";
import { FormButton, SpinnerOverlay } from "components";
import {
  FormikDateInput,
  FormikSelectInput,
  FormikTextInput,
} from "components/UI/Inputs/FormikInputs";
import { Form, Formik } from "formik";
import * as yup from "yup";
import { useRecoilValue, useSetRecoilState } from "recoil";
import { snackbarData, userCredentials } from "recoilState/GlobalState";
import styles from "styles/pages/ModalActions.module.scss";
import { usuarioBasicosGet } from "services/services-mia";
import { RNHTituloGet } from "services/services-mia/programa-salud/personal/RNHTituloGet";
import { LARGO, mensajeLargo } from "consts/limites-campos";
import {
  fechaNacimientoMaxima,
  fechaNacimientoMinima,
  fechaNacimientoValida,
  MENSAJE_FECHA_NACIMIENTO,
} from "consts/fecha-nacimiento";
import {
  ASIGNACIONES,
  GENEROS,
  LARGO_AGENTE,
  MATRICULA_NO_APLICA,
  TIPOS_HORARIO,
  esSinCargo,
  requiereMatricula,
} from "../consts";
import { RNHAgenteIns } from "services/services-mia/programa-salud/personal/RNHAgenteIns";
import { RNHCargoGet } from "services/services-mia/programa-salud/personal/RNHCargoGet";
import { RNHConvenioGet } from "services/services-mia/programa-salud/personal/RNHConvenioGet";

const PASO = { BUSQUEDA: 1, CARGA: 2 };

/**
 * Alta de un agente.
 *
 * Arranca por una BUSQUEDA EN MIA con documento, sexo y fecha de nacimiento: si la
 * persona ya existe como usuario, sus datos personales vienen de ahi y no se tipean.
 * Hasta que esa busqueda no resuelve, el resto del formulario queda apagado.
 *
 * LO UNICO REQUERIDO es lo que identifica a la persona: documento, fecha de
 * nacimiento, genero, nombres y apellidos. Todo el vinculo laboral es opcional y se
 * puede completar despues desde Datos laborales.
 *
 * LOS SELECTORES MANDAN null, NO "". Para los servicios de agente la cadena vacia se
 * GRABA y el null significa "no informado".
 *
 * La matricula sin titulo se rechaza (41021), asi que no viaja sola; y si el titulo
 * elegido no la admite, se muestra "N/A" y tampoco viaja.
 */
export default function InsertModal({ closeModal, updateList }) {
  const setSnackBarInfo = useSetRecoilState(snackbarData);
  const credentials = useRecoilValue(userCredentials);
  const formikRef = useRef();

  const [paso, setPaso] = useState(PASO.BUSQUEDA);
  const [personaExistente, setPersonaExistente] = useState(false);
  const [emailPrecargado, setEmailPrecargado] = useState(false);
  const [titulos, setTitulos] = useState([]);
  const [cargos, setCargos] = useState([]);
  const [convenios, setConvenios] = useState([]);

  const [inRequest, setInRequest] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const getTitulos = async () => {
    const { data: result } = await RNHTituloGet(credentials);
    setTitulos(result?.[0]?.rnhTituloId ? result : []);
  };
  const getCargos = async () => {
    const { data: result } = await RNHCargoGet(credentials);
    const lista = result?.[0]?.rnhCargoId ? result : [];
    setCargos(lista);
    const sinCargo = lista.find(esSinCargo);
    if (sinCargo) formikRef.current?.setFieldValue("cargoCod", sinCargo.rnhCargoCod);
  };
  const getConvenios = async () => {
    const { data: result } = await RNHConvenioGet(credentials);
    setConvenios(result?.[0]?.rnhConvenioId ? result : []);
  };

  useEffect(() => {
    (async () => {
      setIsLoading(true)
      await Promise.all([
        getTitulos(),
        getCargos(),
        getConvenios()
      ])
      setIsLoading(false)
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const buscarPersona = async (values) => {
    setInRequest(true);
    const response = await usuarioBasicosGet(
      {
        usuarioNroDeDocumento: values.documento,
        usuarioSexo: values.genero,
        usuarioFechaDeNacimiento: values.fechaNacimiento,
      },
      credentials
    );
    const encontrados = Array.isArray(response?.result) ? response.result : [];

    if (response?.status?.code !== 1 && encontrados.length === 0) {
      setInRequest(false);
      return;
    }

    if (encontrados.length > 1) {
      setSnackBarInfo({
        message:
          "Hay más de una persona con esos datos. Verificá documento, género y fecha de nacimiento.",
        severity: "warning",
        open: true,
      });
      setInRequest(false);
      return;
    }

    let datosDeLaPersona = {};
    if (encontrados.length === 1) {
      const persona = encontrados[0];
      const mail = persona.usuarioMail ?? "";
      datosDeLaPersona = {
        apellidos: persona.usuarioApellidos ?? "",
        nombres: persona.usuarioNombres ?? "",
        email: mail,
      };
      setPersonaExistente(true);
      setEmailPrecargado(!!mail);
    } else {
      setPersonaExistente(false);
      setEmailPrecargado(false);
    }

    /**
     * `resetForm` y no `setFieldValue` + limpiar `touched`, por el `submitCount`.
     *
     * "Continuar" es un submit, así que al llegar al paso 2 el formulario ya cuenta un envío.
     * `useErrorAlSubmit` —la regla de mostrar errores solo después de enviar— lee ese
     * contador, y con `validateOnChange` activo la primera tecla encendía los requeridos de
     * todo el paso 2 sin que nadie hubiera intentado guardar.
     *
     * Limpiar `touched` y `errors` era el arreglo correcto cuando el error salía de
     * `meta.touched`; desde que la regla vive en `submitCount` ya no alcanza.
     *
     * Los datos de la persona van en el MISMO llamado y no por `setFieldValue`: así es una
     * sola operación atómica y no hay que preguntarse si el estado alcanzó a actualizarse.
     */
    formikRef.current.resetForm({
      values: { ...formikRef.current.values, ...datosDeLaPersona },
    });

    setPaso(PASO.CARGA);
    setInRequest(false);
  };

  const volverABusqueda = () => {
    const limpios = personaExistente ? { apellidos: "", nombres: "", email: "" } : {};
    if (personaExistente) {
      setPersonaExistente(false);
      setEmailPrecargado(false);
    }
    formikRef.current.resetForm({
      values: { ...formikRef.current.values, ...limpios },
    });
    setPaso(PASO.BUSQUEDA);
  };

  const crearAgente = async (values) => {
    setInRequest(true)

    const response = await RNHAgenteIns(credentials, {
      nroDocumento: values.documento,
      sexo: values.genero,
      fechaNacimiento: values.fechaNacimiento,
      apellidos: values.apellidos,
      nombres: values.nombres,
      mail: values.email,
      legajo: values.legajo,
      fechaInicio: values.fechaInicio,
      cargoCod: values.cargoCod,
      tituloCod: values.tituloCod,
      matricula: values.tituloCod ? values.matricula : null,
      convenioCod: values.convenioCod,
      horarioTipo: values.horarioTipo,
      esUnico: values.unique,
    })
    setInRequest(false);

    if (!response.ok) return;

    setSnackBarInfo({
      message: "Agente creado correctamente",
      severity: "success",
      open: true,
    });
    formikRef.current.resetForm();
    updateList();
    closeModal();
  };

  const enBusqueda = paso === PASO.BUSQUEDA;
  const bloqueado = enBusqueda || isLoading;

  const validationSchema = yup.object({
    documento: yup
      .string()
      .matches(/^\d*$/, "Solo números")
      .min(7, "Debe tener al menos 7 dígitos")
      .max(8, "Debe tener 8 dígitos como máximo")
      .required("Debe ingresar el número de documento"),
    genero: yup
      .string()
      .oneOf(GENEROS.map(({ cod }) => cod))
      .required("Debe seleccionar el género"),
    fechaNacimiento: yup
      .string()
      .required("Debe ingresar la fecha de nacimiento")
      .test("rango", MENSAJE_FECHA_NACIMIENTO, fechaNacimientoValida),
    ...(!enBusqueda && {
      apellidos: yup
        .string()
        .max(LARGO_AGENTE.apellidos, mensajeLargo(LARGO_AGENTE.apellidos))
        .required("Debe ingresar los apellidos"),
      nombres: yup
        .string()
        .max(LARGO_AGENTE.nombres, mensajeLargo(LARGO_AGENTE.nombres))
        .required("Debe ingresar los nombres"),
      email: yup
        .string()
        .email("Debe ingresar un email válido")
        .max(LARGO_AGENTE.email, mensajeLargo(LARGO_AGENTE.email)),
      legajo: yup.string().max(LARGO_AGENTE.legajo, mensajeLargo(LARGO_AGENTE.legajo)),
      fechaInicio: yup.string(),
      convenioCod: yup.string(),
      tituloCod: yup.string(),
      unique: yup.string(),
      matricula: yup
        .string()
        .max(LARGO_AGENTE.matricula, mensajeLargo(LARGO_AGENTE.matricula))
        .when("tituloCod", {
          is: (tituloCod) => requiereMatricula(titulos, tituloCod),
          then: yup.string().required("El título seleccionado requiere matrícula"),
          otherwise: yup.string(),
        }),
      cargoCod: yup.string(),
      horarioTipo: yup.string(),
    }),
  });

  return (
    <Formik
      innerRef={formikRef}
      initialValues={{
        documento: "",
        genero: "",
        fechaNacimiento: "",
        apellidos: "",
        nombres: "",
        email: "",
        legajo: "",
        fechaInicio: "",
        convenioCod: "",
        tituloCod: "",
        matricula: MATRICULA_NO_APLICA,
        unique: "",
        cargoCod: "",
        horarioTipo: "",
      }}
      validationSchema={validationSchema}
      onSubmit={(values) => {
        if (enBusqueda) {
          buscarPersona(values);
          return;
        }
        crearAgente(values);
      }}
    >
      {({ values }) => {
        const pideMatricula = requiereMatricula(titulos, values.tituloCod);

        return (
          <Form className={styles.inputs} noValidate>
            <div
              className={styles["column"]}
              style={{ width: enBusqueda ? "420px" : "780px", maxWidth: "100%" }}
            >
              <Grid
                container
                spacing={3}
                sx={{
                  alignItems: "flex-start",
                  width: "100%",
                  "& .MuiFormControl-root": { marginTop: 0, marginBottom: 0 },
                }}
              >
                <Grid
                  item
                  xs={12}
                  md={enBusqueda ? 12 : 6}
                  sx={{ position: "relative" }}
                >
                  <h3 className={styles.sectionTitle}>Datos personales</h3>
                  <Grid
                    container
                    rowSpacing={0}
                    columnSpacing={2}
                    sx={{ paddingTop: "1.75rem" }}
                  >
                    <Grid item xs={12}>
                      <FormikTextInput
                        name="documento"
                        labelText="Número de documento"
                        disabled={!enBusqueda}
                        inputProps={{ maxLength: 8 }}
                        onChange={(e) =>
                          formikRef.current.setFieldValue(
                            "documento",
                            e.target.value.replace(/\D/g, "")
                          )
                        }
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <FormikSelectInput
                        name="genero"
                        labelText="Género"
                        disabled={!enBusqueda}
                      >
                        <option value="" disabled>
                          Seleccione
                        </option>
                        {GENEROS.map(({ cod, label }) => (
                          <option key={cod} value={cod}>
                            {label}
                          </option>
                        ))}
                      </FormikSelectInput>
                    </Grid>
                    <Grid item xs={6}>
                      <FormikDateInput
                        name="fechaNacimiento"
                        labelText="Fecha de nacimiento"
                        disabled={!enBusqueda}
                        inputProps={{
                          min: fechaNacimientoMinima(),
                          max: fechaNacimientoMaxima(),
                        }}
                      />
                    </Grid>

                    {!enBusqueda && (
                      <>
                        <Grid item xs={6}>
                          <FormikTextInput
                            name="apellidos"
                            inputProps={{ maxLength: LARGO.nombre }}
                            labelText="Apellidos"
                            disabled={bloqueado || personaExistente}
                          />
                        </Grid>
                        <Grid item xs={6}>
                          <FormikTextInput
                            name="nombres"
                            inputProps={{ maxLength: LARGO.nombre }}
                            labelText="Nombres"
                            disabled={bloqueado || personaExistente}
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <FormikTextInput
                            name="email"
                            inputProps={{ maxLength: LARGO_AGENTE.email }}
                            labelText="Email"
                            placeholder="ejemplo@mail.com"
                            disabled={bloqueado}
                            helperText={
                              personaExistente && !emailPrecargado
                                ? "La persona no tiene email registrado"
                                : undefined
                            }
                          />
                        </Grid>
                      </>
                    )}
                  </Grid>
                </Grid>

                {!enBusqueda && (
                  <Grid item xs={12} md={6} sx={{ position: "relative" }}>
                    <h3 className={styles.sectionTitle}>Datos laborales</h3>
                    <Grid
                      container
                      rowSpacing={0}
                      columnSpacing={2}
                      sx={{ paddingTop: "1.75rem" }}
                    >
                      <Grid item xs={6}>
                        <FormikTextInput
                          name="legajo"
                          inputProps={{ maxLength: LARGO_AGENTE.legajo }}
                          labelText="Legajo"
                          disabled={bloqueado}
                        />
                      </Grid>
                      <Grid item xs={6}>
                        <FormikSelectInput
                          name="convenioCod"
                          labelText="Régimen Horario"
                          disabled={bloqueado}
                        >
                          <option value="">Sin asignar</option>
                          {convenios.map(({ rnhConvenioCod, rnhConvenioLey, rnhConvenioCargaSemanal }) => (
                            <option key={rnhConvenioCod} value={rnhConvenioCod}>
                              {[rnhConvenioLey && `Ley ${rnhConvenioLey} - ${rnhConvenioCargaSemanal}hs`]
                                .filter(Boolean)
                                .join(" · ")}
                            </option>
                          ))}
                        </FormikSelectInput>
                      </Grid>
                      <Grid item xs={6}>
                        <FormikSelectInput
                          name="tituloCod"
                          labelText="Título"
                          disabled={bloqueado}
                          onChange={(e) => {
                            const tituloCod = e.target.value;
                            formikRef.current.setFieldValue("tituloCod", tituloCod);
                            formikRef.current.setFieldValue(
                              "matricula",
                              requiereMatricula(titulos, tituloCod)
                                ? ""
                                : MATRICULA_NO_APLICA
                            );
                          }}
                        >
                          <option value="">Sin asignar</option>
                          {titulos.map(({ rnhTituloCod, rnhTituloNombre }) => (
                            <option key={rnhTituloCod} value={rnhTituloCod}>
                              {rnhTituloNombre}
                            </option>
                          ))}
                        </FormikSelectInput>
                      </Grid>
                      <Grid item xs={6}>
                        <FormikTextInput
                          name="matricula"
                          inputProps={{ maxLength: LARGO_AGENTE.matricula }}
                          labelText={
                            <>
                              Matrícula
                              {!pideMatricula && !!values.tituloCod && (
                                <small
                                  style={{
                                    fontSize: "0.9rem",
                                    color: "#666",
                                    marginLeft: "0.4rem",
                                    whiteSpace: "nowrap",
                                    fontWeight: 'lighter'
                                  }}
                                >
                                  No requerida
                                </small>
                              )}
                            </>
                          }
                          disabled={bloqueado || !pideMatricula}
                        />
                      </Grid>
                      <Grid item xs={6}>
                        <FormikDateInput
                          name="fechaInicio"
                          labelText="Fecha de inicio"
                          disabled={bloqueado}
                        />
                      </Grid>
                      <Grid item xs={6}>
                        <FormikSelectInput
                          name="unique"
                          labelText="Asignación"
                          disabled={bloqueado}
                        >
                          <option value="">Sin asignar</option>
                          {ASIGNACIONES.map(({ cod, label }) => (
                            <option key={cod} value={cod}>
                              {label}
                            </option>
                          ))}
                        </FormikSelectInput>
                      </Grid>
                      <Grid item xs={6}>
                        <FormikSelectInput
                          name="cargoCod"
                          labelText="Cargo"
                          disabled={bloqueado}
                        >
                          <option value="" disabled>
                            Seleccione
                          </option>
                          {cargos.map(({ rnhCargoCod, rnhCargoNombre }) => (
                            <option key={rnhCargoCod} value={rnhCargoCod}>
                              {rnhCargoNombre}
                            </option>
                          ))}
                        </FormikSelectInput>
                      </Grid>
                      <Grid item xs={6}>
                        <FormikSelectInput
                          name="horarioTipo"
                          labelText="Tipo de Horario"
                          disabled={bloqueado}
                        >
                          <option value="">Sin asignar</option>
                          {TIPOS_HORARIO.map(({ cod, label }) => (
                            <option key={cod} value={cod}>
                              {label}
                            </option>
                          ))}
                        </FormikSelectInput>
                      </Grid>
                    </Grid>
                  </Grid>
                )}
              </Grid>

              <FormButton
                inRequest={inRequest || isLoading}
                newFormData={{ button: enBusqueda ? "Continuar" : "Crear" }}
                closeButtonText={enBusqueda ? "Cancelar" : "Volver"}
                onSecondaryClick={enBusqueda ? undefined : volverABusqueda}
                onCloseModal={closeModal}
              />
              {(inRequest || isLoading) && <SpinnerOverlay />}
            </div>
          </Form>
        );
      }}
    </Formik>
  );
}
